<script setup>
const route = useRoute();
const router = useRouter();
const nuxtApp = useNuxtApp();
const emitter = useEmitter();
const localePath = useLocalePath();

const { locale } = useI18n();
const { fetchPage } = useFetchAPI();
const { isSearchOpen, setIsSearchOpen } = useMode();
const { searchKeywords, setKeywords } = useSearchKeywords();

const keyword = computed({
    get() {
        return searchKeywords.value;
    },
    set(val) {
        setKeywords(val);
    },
});

const searchData = computed(() => {
    return (
        nuxtApp.payload.data?.["fetchPage-search"]?.[locale.value] ||
        nuxtApp.payload.data?.["fetchPage-search"]?.zh
    );
});

if (!searchData.value) await fetchPage("search");

const searchInput = ref(null);

const close = () => {
    setIsSearchOpen(false);
};

const isComposing = ref(false);
const search = () => {
    if (keyword.value && !isComposing.value) {
        setIsSearchOpen(false);
        emitter.emit("close-menu", false);
        if (route.path !== localePath("/search")) {
            router.push(localePath(`/search`));
        } else {
            emitter.emit("perform-search", keyword.value);
        }
    }
};

const handleKeyDown = (event) => {
    if (event.key === "Enter" && !isComposing.value) {
        search();
    }
};

const cancelSearch = () => {
    keyword.value = "";
    nextTick(() => {
        searchInput.value?.focus();
    });
};

onMounted(() => {
    watch(isSearchOpen, (val) => {
        if (val) {
            document.body.style.overflow = "hidden";
            nextTick(() => {
                searchInput.value.focus();
            });
        } else {
            document.body.style.overflow = "auto";
        }
    });
});
</script>

<template>
    <div id="search-modal" class="fixed left-0 top-0 z-[9999] h-full w-full">
        <div
            id="modal-mask"
            class="absolute left-0 top-0 h-full w-full bg-base-black-80"
            @click="close"
        ></div>
        <div
            id="modal-body"
            class="relative flex w-full items-center justify-center bg-base-bgGrey"
        >
            <button
                @click="close"
                class="_button-close flex cursor-pointer items-center justify-center"
            >
                <SvgClose24
                    class="h-[6.1vw] w-[6.1vw] xs:h-[24px] xs:w-[24px]"
                />
            </button>
            <div
                class="close-outside-click relative flex w-full flex-col gap-[7.63vw] px-[6.1vw] py-[calc(var(--window-height)*0.1093)] xs:gap-[30px] xs:px-[24px] sm:px-[5.21vw] sm:py-[calc(var(--window-height)*0.15)] lg:max-w-[932px] lg:justify-center lg:px-0 lg:py-40"
            >
                <h2 class="slt-h3-ttd px-[2.03vw] xs:px-2">
                    <span
                        v-for="title in searchData?.meta_box?.title"
                        :key="title"
                        class="block"
                        >{{ title }}</span
                    >
                </h2>
                <div
                    class="slt-focus-outline flex items-center gap-[3.05vw] rounded-[18.32vw] bg-base-silver px-[5.09vw] py-[4.07vw] xs:gap-3 xs:rounded-[72px] xs:px-5 xs:py-4 sm:max-w-[452px] lg:max-w-[464px]"
                >
                    <SvgSearch24
                        class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                        :color="'black'"
                        @click="search"
                        v-sendGAsignal="{
                            event_category: 'search modal',
                            event_label: 'search',
                        }"
                    />
                    <input
                        type="text"
                        :placeholder="searchData?.meta_box?.placeholder"
                        ref="searchInput"
                        class="slt-placeholder w-full flex-1 bg-transparent text-base-black placeholder:text-base-black-40"
                        @keydown="handleKeyDown"
                        @compositionstart="isComposing = true"
                        @compositionend="isComposing = false"
                        v-model="keyword"
                    />
                    <SvgClose24
                        v-show="keyword"
                        class="h-[6.1vw] w-[6.1vw] cursor-pointer xs:h-[24px] xs:w-[24px]"
                        :color="'#FF6400'"
                        @click="cancelSearch"
                        v-sendGAsignal="{
                            event_category: 'search modal',
                            event_label: 'cancel search',
                        }"
                    />
                </div>
                <div class="px-[2.03vw] xs:px-2">
                    <h5 class="slt-h5 mb-[5.09vw] xs:mb-5">
                        <span
                            v-for="title in searchData?.meta_box?.fastLinks
                                .title"
                            :key="title"
                            class="block"
                            >{{ title }}</span
                        >
                    </h5>
                    <ul
                        class="slt-body-1 -ml-4 flex flex-col gap-[3.05vw] xs:gap-3"
                    >
                        <li
                            v-for="data in searchData?.meta_box?.fastLinks
                                ?.data"
                            :key="data"
                        >
                            <NuxtLink
                                class="btn flex w-fit items-center gap-[3.05vw] rounded-full py-1 pl-4 pr-5 transition-all duration-300 ease-in-out xs:gap-3 sm:hover:bg-base-white"
                                :to="$localePath(data?.link)"
                                :external="data?.type === 'external'"
                                :target="
                                    data?.type === 'external'
                                        ? '_blank'
                                        : '_self'
                                "
                                v-sendGAsignal="{
                                    event_category: 'search modal',
                                    event_label: data?.name?.join(''),
                                }"
                            >
                                <div class="btn__arrow__group">
                                    <SvgArrowForward20
                                        :color="'black'"
                                        class="btn__arrow btn__arrow--main"
                                    />
                                    <SvgArrowForward20
                                        :color="'black'"
                                        class="btn__arrow btn__arrow--hover"
                                    />
                                </div>
                                <span
                                    v-for="text in data?.name"
                                    :key="text"
                                    class="block"
                                    >{{ text }}</span
                                >
                            </NuxtLink>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.show-search-modal-enter-active,
.show-search-modal-leave-active {
    @apply transition-opacity duration-700 ease-linear;
}

.show-search-modal-enter-active #modal-body,
.show-search-modal-leave-active #modal-body {
    @apply transition-transform duration-500 ease-in-out-quint;
}

.show-search-modal-enter-from #modal-body,
.show-search-modal-leave-to #modal-body {
    transform: translateY(-100%);
}

.show-search-modal-enter-to #modal-body,
.show-search-modal-leave-from #modal-body {
    transform: translateY(0%);
}

.show-search-modal-enter-active #modal-mask,
.show-search-modal-leave-active #modal-mask {
    @apply transition-opacity duration-500 ease-linear;
}

.show-search-modal-enter-from #modal-mask,
.show-search-modal-leave-to #modal-mask {
    opacity: 0;
}

.show-search-modal-enter-to #modal-mask,
.show-search-modal-leave-from #modal-mask {
    opacity: 1;
}

._button-close {
    @apply absolute right-[4.07vw] top-[4.07vw] z-[5] h-[10.18vw] rounded-full bg-base-doveGrey px-[4.07vw] xs:right-[16px] xs:top-[16px] xs:h-[40px] xs:px-[16px] sm:right-[24px] sm:top-[24px] lg:right-[40px] lg:top-[40px] lg:h-[48px] lg:px-[20px];
}

/* button arrow animation */
.btn__arrow__group {
    @apply relative inline-block h-[5.09vw] w-[5.09vw] overflow-hidden align-middle xs:h-[20px] xs:w-[20px];
}
.btn__arrow {
    @apply absolute right-0 top-0 size-full;
    transition:
        transform 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33),
        opacity 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33);
}
.btn__arrow--hover {
    transform: translate3d(-100%, 0, 0);
    opacity: 0;
}

@media (min-width: 640px) {
    .btn:hover .btn__arrow--main {
        transform: translate3d(100%, 0, 0);
        opacity: 0;
    }
    .btn:hover .btn__arrow--hover {
        transform: translate3d(0, 0, 0);
        opacity: 1;
    }
}
</style>
